import Service, {
  ServiceCreator,
  ServiceDeleter,
  ServiceReader,
  ServiceUpdater,
} from "..";
import PrivateUser from "../../dtos/user";
import { UserRegisterInput, UserUpdateInput } from "../../dtos/user/input";
import UserAuthApi from "../../api/user/auth";
import UserApi from "../../api/user";
import OtpUserApi from "../../api/user/otp";
import {
  UserOtpRegenerateInput,
  UserOtpValidateInput,
} from "../../dtos/user/otp";
import { UserLoginInput } from "../../dtos/user/auth";
import { ApiError } from "../../api";

class UserServiceReader extends ServiceReader {
  public async current(): Promise<PrivateUser | null> {
    const api = new UserAuthApi();
    try {
      return await api.get();
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) return null;
      throw error;
    }
  }

  public async newOtp(action: string): Promise<string> {
    const api = new OtpUserApi();
    return (await api.get(null, new UserOtpRegenerateInput(action))).action;
  }
}

class UserServiceCreator extends ServiceCreator {
  public async login(
    recaptchaToken: string,
    emailOrPublicId: string,
    password: string
  ): Promise<string> {
    const api = new UserAuthApi();
    return (await api.post(new UserLoginInput(emailOrPublicId, password)))
      .token;
  }

  public async register(
    recaptchaToken: string,
    email: string,
    public_id: string,
    password: string,
    first_name: string,
    last_name: string,
    lc_id: string,
    year: number,
    is_medicine: boolean,
    programme_title: string,
    series: number,
    group: number,
    registration_number: string,
    id_image: File
  ): Promise<string> {
    const api = new UserApi();
    return (
      await api.post(
        new UserRegisterInput(
          recaptchaToken,
          email,
          public_id,
          password,
          first_name,
          last_name,
          lc_id,
          year,
          is_medicine,
          programme_title,
          series,
          group,
          registration_number,
          id_image
        )
      )
    ).action;
  }

  public async finishOtp(action: string, otp: string): Promise<PrivateUser> {
    const api = new OtpUserApi();
    return await api.post(new UserOtpValidateInput(action, otp));
  }
}

class UserServiceUpdater extends ServiceUpdater {
  public async subscribeTransmed(): Promise<void> {
    // TODO
    return;
  }

  public async user(
    id: number,
    current_passowrd: string,
    email: string,
    public_id: string,
    password: string,
    first_name: string,
    last_name: string,
    lc_id: string,
    year: number,
    is_medicine: boolean,
    programme_title: string,
    series: number,
    group: number,
    registration_number: string,
    id_image?: File
  ): Promise<string | null> {
    const api = new UserApi();
    const response = await api.put(
      `${id}`,
      new UserUpdateInput(
        current_passowrd,
        email,
        public_id,
        password,
        first_name,
        last_name,
        lc_id,
        year,
        is_medicine,
        programme_title,
        series,
        group,
        registration_number,
        id_image
      )
    );

    if (response instanceof PrivateUser) return null;
    return response.action;
  }

  public async password(
    recaptchaToken: string,
    emailOrPublicId: string,
    password: string
  ): Promise<string> {
    const api = new UserAuthApi();
    return (await api.put("", new UserLoginInput(emailOrPublicId, password)))
      .action;
  }
}

class UserServiceDeleter extends ServiceDeleter {
  public async logout(): Promise<void> {
    const api = new UserAuthApi();
    await api.delete();
  }

  public async validationProblems(id: number): Promise<PrivateUser> {
    return await new UserApi().delete(`${id}`);
  }
}

export default class UserService extends Service {
  public get read(): UserServiceReader {
    return new UserServiceReader();
  }
  public get create(): UserServiceCreator {
    return new UserServiceCreator();
  }
  public get update(): UserServiceUpdater {
    return new UserServiceUpdater();
  }
  public get delete(): UserServiceDeleter {
    return new UserServiceDeleter();
  }
}
